import { StandardEvents } from '@shopify/events';

/**
 * Shows the description for the selected value of a variant option (see blocks/option-description.liquid).
 * On a variant change, copies its new contents from the section HTML the variant picker already fetched,
 * and toggles between the short and full text.
 */
class OptionDescription extends HTMLElement {
  /** @type {Element | null} */
  #target = null;

  connectedCallback() {
    this.addEventListener('click', this.#handleClick);
    this.#target = this.closest('[id*="ProductInformation-"]');
    this.#target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#handleClick);
    this.#target?.removeEventListener(StandardEvents.productSelect, this.#handleProductSelect);
    this.#target = null;
  }

  /**
   * @param {Event} event
   */
  #handleClick = (event) => {
    const toggle = /** @type {Element} */ (event.target).closest('.option-description__toggle');
    if (!toggle) return;

    const short = this.querySelector('.option-description__short');
    const full = this.querySelector('.option-description__full');
    if (!(short instanceof HTMLElement) || !(full instanceof HTMLElement)) return;

    const expand = toggle.getAttribute('aria-expanded') === 'false';
    short.hidden = expand;
    full.hidden = !expand;

    // Keep keyboard focus on the button that's now visible.
    const visibleToggle = (expand ? full : short).querySelector('.option-description__toggle');
    if (visibleToggle instanceof HTMLElement) visibleToggle.focus();
  };

  /**
   * @param {any} event - The product select event
   */
  #handleProductSelect = (event) => {
    event.promise
      ?.then((/** @type {any} */ { detail }) => {
        if (!detail?.html) return;
        if (detail.newProduct) this.dataset.productId = detail.newProduct.id;
        if (detail.productId && detail.productId !== this.dataset.productId) return;

        const updated = detail.html.getElementById(this.id);
        if (!updated) return;
        this.innerHTML = updated.innerHTML;
        this.hidden = updated.hasAttribute('hidden');
      })
      .catch((/** @type {any} */ error) => {
        if (error?.name !== 'AbortError') console.warn('[option-description] Event promise rejected:', error);
      });
  };
}

if (!customElements.get('option-description')) {
  customElements.define('option-description', OptionDescription);
}
